import '@enuygun/design-system/style.css'
import '@enuygun/frontend-components/style.css'
import '@splidejs/react-splide/css'
import '@splidejs/react-splide/css/core'

// Filter out useLayoutEffect warnings (both server and client)
if (process.env.NODE_ENV === 'development') {
  const originalWarn = console.warn
  const originalError = console.error

  const shouldFilter = (args: any[]) => {
    const message = args[0]
    const fullMessage = args.join(' ')
    return (
      (typeof message === 'string' && (message.includes('useLayoutEffect does nothing on the server') || message.includes('useLayoutEffect should only be used in components'))) ||
      (typeof fullMessage === 'string' &&
        (fullMessage.includes('useLayoutEffect does nothing on the server') || fullMessage.includes('useLayoutEffect should only be used in components')))
    )
  }

  console.warn = (...args: any[]) => {
    if (shouldFilter(args)) return
    originalWarn.apply(console, args)
  }

  console.error = (...args: any[]) => {
    if (shouldFilter(args)) return
    originalError.apply(console, args)
  }
}

import { FC, useEffect, useState, useRef } from 'react'

import { EndesignProvider, ToastProvider } from '@enuygun/design-system'
import { AuthAPI, Description, EndesignStore, NapiClient } from '@enuygun/frontend-components'
import { appWithTranslation } from 'next-i18next'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'

import Header from 'components/Header'
import { OrganizationSchema } from 'components/Schemas'
import { GlobalStyle } from 'components/styled'
import Tracking from 'components/Tracking'
import { useRedirectionBanner } from 'hooks/useRedirectionBanner'
import { WingieRedirectionBanner } from '@enuygun/frontend-components'
import { AppStateProvider, type Markets } from 'context/AppContext'
import { UserDeviceProvider } from 'context/UserAgent'
import { FlightAPI } from 'services/rest'
import { checkIsMobileApp } from 'utils/checkIsMobileApp'
import { DayJsLanguage } from 'utils/dayjs'
import { openSans, notoSansArabic } from 'utils/fonts'
import { modifyLinksInPage } from 'utils/modifyLinksInPage'

import Script from 'next/script'
import CookieAlert from 'components/CookieAlert'
import Footer from 'components/Footer'
import { AdvertStateProvider } from 'context/AdvertContext'
import { PRODUCT_CATEGORIES } from 'enums/categories'
import { useIsMobile } from 'hooks/useIsMobile'
import { useDeviceType } from 'hooks/useDeviceType'
import { useIsomorphicLayoutEffect } from 'hooks/useIsomorphicLayoutEffect'
import { domainURL } from 'utils/domainURL'
import { handleOneTrustLoad } from 'utils/onetrust'
import { getCookieYesProvider, isImpliedConsentCookieYes, resolveConsentProvider } from 'utils/consentProvider'
import useCookieYesConsent from 'hooks/useCookieYesConsent'
import useCookieYesAcceptAllButton from 'hooks/useCookieYesAcceptAllButton'
import useCookieYesDefaultToggles from 'hooks/useCookieYesDefaultToggles'
import useCookieYesGtmGate from 'hooks/useCookieYesGtmGate'
import { getGtmBootstrapScript, getGtmContainerId } from 'utils/gtms'
import nextI18nextConfig from '../../next-i18next.config'
import getConfig from 'next/config'
import GoogleOneTapWrapper from 'components/GoogleOneTap/GoogleOneTapWrapper'
import { applyDirectFlightLabelExperiment, DIRECT_FLIGHT_LABEL_QUERY_OVERRIDE_KEY, resolveDirectFlightLabelVariantFromOverride } from 'utils/directFlightLabelExperiment'
import { getHomepageUiVariantsFromDocument, type HomepageUiVariants } from 'utils/uiVariants'

// AI ile çevrilmiş 24 dil
const AI_TRANSLATED_LANGUAGES = ['it', 'fr', 'pt', 'ro', 'pl', 'lt', 'uz', 'sr', 'ky', 'tg', 'kk', 'nl', 'zh', 'ka', 'hi', 'ja', 'ko', 'ur', 'ta', 'th']

const AI_TRANSLATION_MESSAGES = {
  it: '*Questo contenuto è stato tradotto con l’aiuto della tecnologia di intelligenza artificiale.',
  fr: '*Ce contenu a été traduit à l’aide de la technologie de l’intelligence artificielle.',
  pt: '*Este conteúdo foi traduzido com a ajuda da tecnologia de inteligência artificial.',
  ro: '*Acest conținut a fost tradus cu ajutorul tehnologiei de inteligență artificială.',
  pl: '*Ta treść została przetłumaczona przy użyciu technologii sztucznej inteligencji.',
  lt: '*Šis turinys buvo išverstas naudojant dirbtinį intelektą.',
  uz: '*Ushbu kontent sun’iy intellekt texnologiyasi yordamida tarjima qilingan.',
  sr: '*Ovaj sadržaj je preveden pomoću tehnologije veštačke inteligencije.',
  ky: '*Бул мазмун жасалма интеллект технологиясынын жардамы менен которулган.',
  tg: '*Ин муҳтаво бо истифода аз технологияи зеҳни сунъӣ тарҷума шудааст.',
  kk: '*Бұл мазмұн жасанды интеллект технологиясы арқылы аударылған.',
  nl: '*Deze inhoud is vertaald met behulp van kunstmatige intelligentie.',
  en: '*This content has been translated with artificial intelligence technology.',
  zh: '*此内容已借助人工智能技术进行翻译.',
  ka: '*ეს კონტენტი თარგმნილია ხელოვნური ინტელექტის ტექნოლოგიის დახმარებით.',
  hi: '*यह सामग्री कृत्रिम बुद्धिमत्ता तकनीक की सहायता से अनुवादित की गई है।',
  ja: '*このコンテンツは人工知能技術の助けを借りて翻訳されました。',
  ko: '*이 콘텐츠는 인공지능 기술의 도움을 받아 번역되었습니다.',
  ur: '*یہ مواد مصنوعی ذہانت ٹیکنالوجی کی مدد سے ترجمہ کیا گیا ہے۔',
  ta: '*இந்த உள்ளடக்கம் செயற்கை நுண்ணறிவு தொழில்நுட்பத்தின் உதவியுடன் மொழிபெயர்க்கப்பட்டது.',
  th: '*เนื้อหานี้ได้รับการแปลด้วยความช่วยเหลือของเทคโนโลยีปัญญาประดิษฐ์',
}

const App: FC<AppProps<any> & { uiVariants?: HomepageUiVariants | null }> = ({ Component, pageProps, uiVariants }): JSX.Element => {
  const { publicRuntimeConfig } = getConfig()

  const { market, domain, currency, language, error, host, fullLocale, path_locale, endpoint, expansionLocale, redirection } = pageProps
  const router = useRouter()
  const isInvoicePage = router.pathname === '/invoice'
  const isInvoiceViewPage = router.pathname === '/invoice/view'
  const isFavoritesPage = router.pathname.includes('/favori-paylas')
  const $errorPage = error

  const processEnv = publicRuntimeConfig?.processEnv
  const DynamicCoreApiUrl = processEnv?.NEXT_PUBLIC_DYNAMIC_CORE_API || processEnv?.DYNAMIC_CORE_API || process.env.NEXT_PUBLIC_DYNAMIC_CORE_API

  const Page = Component as any

  const { locale, query } = useRouter()

  const $category = (query?.form || 'ucak-bileti') as keyof typeof PRODUCT_CATEGORIES

  const userAgentContext = pageProps?.ua

  // Determine if we should use Arabic font (when language is Arabic)
  const isArabicMarket = language === 'ar'
  const fontFamily = isArabicMarket ? notoSansArabic.style.fontFamily : openSans.style.fontFamily
  const fontClassName = isArabicMarket ? notoSansArabic.className : openSans.className

  useIsomorphicLayoutEffect(() => {
    FlightAPI.defaults.baseURL = pageProps?.endpoint
  }, [])

  const isMobile = useIsMobile()
  const { isTablet } = useDeviceType()

  const noIndex = pageProps?.noIndex
  // TODO: add all locale to replace statement
  // const locale = 'tr'
  // TODO: When coding amp page add this Attr.
  const amphtml = pageProps?.ampHtml

  const endesignLocaleBase = pageProps?.endesignLocales

  const clientUiVariants = typeof window !== 'undefined' ? getHomepageUiVariantsFromDocument() : null
  const resolvedUiVariants = uiVariants || clientUiVariants
  const directFlightLabelQueryOverride = typeof query?.[DIRECT_FLIGHT_LABEL_QUERY_OVERRIDE_KEY] === 'string' ? query[DIRECT_FLIGHT_LABEL_QUERY_OVERRIDE_KEY] : null
  const directFlightLabelVariant = resolveDirectFlightLabelVariantFromOverride(directFlightLabelQueryOverride, resolvedUiVariants?.directFlightLabelVariant || 'A')
  const searchFormVariant = resolvedUiVariants?.searchFormVariant || 'A'
  const endesignLocale =
    applyDirectFlightLabelExperiment(endesignLocaleBase, {
      variant: directFlightLabelVariant,
      market,
      language,
    }) ?? undefined

  const params = pageProps.params
  const isMobileApp = checkIsMobileApp(params) || router.query.isMobileApp === '1'

  useEffect(() => {
    if (isMobileApp) {
      modifyLinksInPage()
    }
  }, [])

  // Host bazlı consent sağlayıcısı (geçerli CookieYes id varsa CookieYes, yoksa OneTrust) — bkz. utils/consentProvider
  const consentProvider = resolveConsentProvider(host)
  const cookieYes = getCookieYesProvider(consentProvider)
  const isOneTrustHost = consentProvider?.provider === 'onetrust'
  // KVKK örtülü onay modeli yalnızca 'implied' host'larda (www.enuygun.com); GDPR host'ları (wingie.de) CookieYes varsayılanında kalır
  const isImpliedCookieYes = isImpliedConsentCookieYes(consentProvider)

  // CookieYes (implied): sayfa geçişinde örtülü kabul + Google Consent Mode senkronu
  useCookieYesConsent(isImpliedCookieYes)
  // OneTrust handleOneTrustLoad: karar yokken C0002/C0003/C0004 anahtarları kapalı gelsin
  useCookieYesDefaultToggles(isImpliedCookieYes)
  // enuygunsigorta.com ile aynı banner tasarımı: tercih merkezine "Tümüne İzin Ver" butonu
  useCookieYesAcceptAllButton(!!cookieYes?.bannerCustomizations)
  // Tüm tracking'i reddetmiş kullanıcıda GTM bootstrap yüklenmez (kasko / core-frontend paritesi)
  const gtmAllowed = useCookieYesGtmGate(isImpliedCookieYes)
  const gtmContainerId = getGtmContainerId(market)
  const isLocal = process.env.NODE_ENV === 'development'

  useEffect(() => {
    if (typeof window === 'undefined' || !isOneTrustHost) return

    window.OptanonWrapper = () => {
      handleOneTrustLoad()
    }
  }, [isOneTrustHost])

  const $language = (language?.includes('-') ? language.split('-')[1] : language) || (locale?.includes('-') ? locale.split('-')[1] : locale) || 'tr'
  DayJsLanguage($language, market)

  const $headerProps = {
    app: false,
    shape: ($errorPage ? 'default' : 'ghost') as 'default' | 'ghost',
    membership: $errorPage ? false : true,
    domain: domain,
    locale: locale || fullLocale,
    language: $language,
    market: market,
    currency: currency,
    notification: false,
    errorPage: $errorPage,
  }

  const alternate = pageProps?.res && pageProps.res[3] ? pageProps.res[3].hreflangLinks : null
  //TODO::
  const canonical = pageProps?.res && pageProps.res[3] ? pageProps?.res[3].canonical : market !== 'tr' ? `${domainURL[market as Markets]}/` : ''

  const domainUrl = domainURL[market as Markets]
  const ACCOUNT_PATH = domainUrl?.includes('www.enuygun') ? '/uyelik' : '/account'
  const AUTH_API_URL = market === 'tr' ? `${host}${ACCOUNT_PATH}` : `${endpoint}${ACCOUNT_PATH}`

  NapiClient.defaults.baseURL = market === 'tr' ? `${host}/napi/v1` : `${endpoint}/napi/v1`
  AuthAPI.defaults.baseURL = AUTH_API_URL
  // AI çeviri kontrolü
  const languageCode = language?.includes('-') ? language.split('-')[1] : language
  const isAITranslated = languageCode ? AI_TRANSLATED_LANGUAGES.includes(languageCode) : false

  // AI çeviri mesajını o dilde al
  const getAITranslationMessage = (langCode: string): string => {
    return AI_TRANSLATION_MESSAGES[langCode as keyof typeof AI_TRANSLATION_MESSAGES] || AI_TRANSLATION_MESSAGES.en
  }

  // Redirection Banner Logic - Query parametrelerini router'dan al
  const isPredirection = router.query.predirection === 'true'
  const fromDomain = router.query.from as string
  const currentHostname = typeof window !== 'undefined' && window.location.hostname

  // fromDomain'i tam URL'ye çevir ve preferDomain parametresi ekle
  const buildSourceDomainUrl = (domain: string): string => {
    if (!domain) return ''
    // Eğer zaten tam URL ise (http:// veya https:// ile başlıyorsa), olduğu gibi kullan
    if (domain.startsWith('http://') || domain.startsWith('https://')) {
      try {
        const url = new URL(domain)
        url.searchParams.set('preferDomain', 'true')
        return url.toString()
      } catch {
        return domain
      }
    }
    // Sadece domain adı ise, temizle ve https:// ile başlat
    const cleanDomain = domain.replace(/^\/+|\/+$/g, '') // Başındaki ve sonundaki slash'leri temizle

    // Subdomain kontrolü: Eğer domain zaten bir subdomain ise (örn: sa.wingie.com), www ekleme
    // Subdomain pattern: 2 harfli kod + .wingie.com veya .enuygun.com (örn: sa.wingie.com, www.wingie.az)
    // veya zaten www. ile başlıyorsa
    const parts = cleanDomain.split('.')
    const isSubdomain =
      parts.length >= 3 && // En az 3 parça varsa (örn: sa.wingie.com)
      (cleanDomain.includes('.wingie.com') || cleanDomain.includes('.enuygun.com')) &&
      !cleanDomain.startsWith('www.') // www. ile başlamıyorsa

    // Eğer zaten www. ile başlıyorsa veya subdomain ise, olduğu gibi kullan
    const finalDomain = cleanDomain.startsWith('www.') || isSubdomain ? cleanDomain : `www.${cleanDomain}`

    return `https://${finalDomain}/?preferDomain=true`
  }

  const sourceDomainUrl = fromDomain ? buildSourceDomainUrl(fromDomain) : ''

  const {
    showBanner,
    sourceDomain,
    targetDomain,
    language: bannerLanguage,
    handleClose,
  } = useRedirectionBanner({
    predirection: isPredirection,
    sourceDomain: sourceDomainUrl || fromDomain,
    targetDomain: currentHostname || '',
    language: $language || 'tr',
  })

  const [bannerHeight, setBannerHeight] = useState(0)
  const bannerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (showBanner) {
      // Observer for size changes of the banner element
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          // contentRect padding'i dahil etmez, bu yüzden getBoundingClientRect kullanıyoruz
          setBannerHeight(entry.target.getBoundingClientRect().height)
        }
      })

      // We need to wait for the child to be mounted/available
      let retryCount = 0
      const MAX_RETRIES = 50 // Approx 800ms-1s depending on frame rate

      const attemptObserve = () => {
        const bannerElement = document.querySelector('[data-testid="wingieredirectionbanner"]') as HTMLElement
        if (bannerElement) {
          // Initial measure
          setBannerHeight(bannerElement.getBoundingClientRect().height)
          resizeObserver.observe(bannerElement)
        } else if (retryCount < MAX_RETRIES) {
          // Retry if not yet available
          retryCount++
          requestAnimationFrame(attemptObserve)
        } else {
          // Silently fail or warn in dev
          if (process.env.NODE_ENV === 'development') {
            console.warn('Redirection banner element not found after max retries.')
          }
        }
      }

      attemptObserve()

      return () => {
        resizeObserver.disconnect()
      }
    } else {
      setBannerHeight(0)
    }
  }, [showBanner])

  return (
    <>
      <Head>
        <link rel="canonical" href={market === 'tr' ? 'https://www.enuygun.com/' : canonical || domainURL[market as Markets] || ''} />
        <meta property="og:url" content={market === 'tr' ? 'https://www.enuygun.com/' : canonical || domainURL[market as Markets] || ''} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta httpEquiv="content-language" content={$language} />
        <meta
          property="og:image"
          content={market === 'tr' ? 'https://cdn2.enuygun.com/media/lib/uploads/image/og-image-50896.jpeg' : 'https://cdn.wingie.com/assets/images/og-image-w.jpg'}
        />
        <meta property="og:site_name" content={market === 'tr' ? 'Enuygun' : 'Wingie'} />
        <meta
          name="google-site-verification"
          content={
            market === 'tr'
              ? 'dDxuyzMQ-fLPb5Ktc-49Soh13sdozrajrAAfO86tLf4'
              : market === 'az'
              ? 'MxhPOQl3TnSIQzv8G1ulUuz0Rj8vh7jpCvBAL5Kgg_0'
              : '1dOmweDIYlqlvka49hu_lp0Wrp502_bk8cFY6Va4dpQ'
          }
        />
        <link
          rel="icon"
          href={market === 'tr' ? 'https://cdn.enuygun.com/bundles/enuygunwebui/img/favicon.ico' : 'https://cdn.enuygun.com/bundles/enuygunwebui/img/favicon-w.ico?v=5'}
        />
        <meta property="og:locale" content={`${$language}_${market?.toUpperCase()}`} />
        {market === 'tr' && <meta name="msvalidate.01" content="D24479907B7A3B42E1434F5DFFCE1497" />}
        {market === 'tr' ? (
          <>
            <meta name="google-play-app" content="app-id=com.mobilatolye.android.enuygun" />
            <meta name="apple-itunes-app" content="app-id=669210250" />
            {/*<link rel="manifest" href="/manifest.json" />*/}
            <link rel="dns-prefetch" href="//www.enuygun.com/" />
            <link rel="dns-prefetch" href="//cdn.enuygun.com/" />
            <link rel="dns-prefetch" href="//cdn2.enuygun.com/" />
          </>
        ) : (
          <>
            <meta name="mobile-web-app-capable" content="yes" />
            <meta name="theme-color" content="#2dc44d" />
            <link rel="apple-touch-icon" href="https://cdn.enuygun.com/bundles/enuygunwebui/img/favicon-w.ico?v=5"></link>
            {alternate?.map((item, index) => (
              <link rel="alternate" key={index} href={item.url} hrefLang={item.locale} />
            ))}
          </>
        )}
        {noIndex && <meta name="robots" content="noindex" />}
        {amphtml && <link rel="amphtml" href={`${amphtml}`} />}
        <OrganizationSchema market={market} />
      </Head>
      <AppStateProvider
        value={{
          market: market,
          fullLocale: fullLocale,
          currencyConversions: pageProps.currencyConversions,
          language: language,
          currency: pageProps?.currency,
          domain: domain,
          endpoint: pageProps?.endpoint,
          host: host,
          path_locale: path_locale,
          expansionLocale: expansionLocale,
          searchFormVariant,
          directFlightLabelVariant,
          headerLinks: pageProps?.headerLinks,
        }}
      >
        <AdvertStateProvider
          value={{
            category: $category,
          }}
        >
          <UserDeviceProvider value={userAgentContext}>
            <EndesignProvider userAgent={userAgentContext}>
              <EndesignStore host={host} locale={endesignLocale} env={{ AUTH_API: AUTH_API_URL, NAPI_URL: NapiClient.defaults.baseURL, DYNAMIC_API: DynamicCoreApiUrl }}>
                <GlobalStyle font={fontFamily} isMobile={isMobile} showBanner={showBanner} isArabicMarket={isArabicMarket} />
                <div style={{ minHeight: '50vh' }} className={fontClassName}>
                  {/* Redirection Banner - En üstte */}
                  {showBanner && !isMobileApp && market !== 'tr' && (
                    <div ref={bannerRef}>
                      <WingieRedirectionBanner refDomain={targetDomain} sourceDomain={sourceDomain} language={bannerLanguage} onClose={handleClose} />
                    </div>
                  )}

                  {!isMobileApp && !isInvoicePage && !isInvoiceViewPage && !(isMobile && isFavoritesPage) && (
                    <div style={{ position: isMobile ? 'fixed' : 'relative', zIndex: 11 }}>
                      <Header {...$headerProps} showBanner={showBanner} bannerHeight={bannerHeight} />
                    </div>
                  )}

                  <Page {...pageProps} />
                  {isAITranslated && (
                    <Description>
                      <p
                        style={{
                          fontSize: '14px',
                          textAlign: 'center',
                          marginTop: '-16px',
                        }}
                      >
                        {getAITranslationMessage(languageCode)}
                      </p>
                    </Description>
                  )}
                </div>
                {/* Google One Tap - TR + Wingie */}
                {!isMobileApp && !isInvoicePage && !isInvoiceViewPage && (
                  <GoogleOneTapWrapper market={market} domain={domain} endpoint={endpoint} host={host} isTablet={isTablet} />
                )}
                {!isMobileApp && !isInvoicePage && !isInvoiceViewPage && !(isMobile && isFavoritesPage) && (
                  <Footer data={pageProps?.footer} footerTrans={endesignLocale?.FooterTrans} />
                )}
                <Tracking market={market} />
                {!isInvoicePage && !isInvoiceViewPage && (
                  <ToastProvider>
                    <CookieAlert />
                  </ToastProvider>
                )}
              </EndesignStore>
            </EndesignProvider>
          </UserDeviceProvider>
        </AdvertStateProvider>
      </AppStateProvider>
      {/*
        GTG: bootstrap first-party /6wz7af34h/ (Wingie /hhri/) gateway varsa bekler, yoksa gtm.js yükler.
        Tekrar statik <script src=".../gtm.js"> veya @next/third-parties GoogleTagManager ekleme.
      */}
      {!isLocal && !isInvoiceViewPage && !isInvoicePage && gtmAllowed && (
        <Script
          id={`googletagmanager-${gtmContainerId}`}
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: getGtmBootstrapScript(gtmContainerId),
          }}
        />
      )}
    </>
  )
}

export default appWithTranslation(App as any, nextI18nextConfig as any)
